import './ProjectListColumnSelector.scss';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { constVoid } from 'fp-ts/function';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { SortHandleIcon } from 'src/modules/common/icons/SortHandleIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { ProjectListColumn, ProjectListColumnKey } from 'src/modules/project-list/types/ProjectListColumn';
import { ProjectListColumnToggle } from 'src/modules/project-list/views/ProjectListColumnToggle';

type Props = {
  readonly columns: ReadonlyArray<ProjectListColumn>;
  readonly onChange: (columns: ReadonlyArray<ProjectListColumn>) => void;
};

export const ProjectListColumnSelector = memo(({ columns, onChange }: Props) => {
  const sortableItems = useMemo(() => columns.map((it) => it.key).map(String), [columns]);
  const sortableSensors = useSensors(useSensor(PointerSensor));
  const sortableStrategy = verticalListSortingStrategy;
  const sortableModifiers = useMemo(() => [restrictToVerticalAxis], []);

  const handleToggle = useCallback((column: ProjectListColumn) => {
    onChange(columns.map((other) => (
      other.key === column.key
        ? column
        : other
    )));
  }, [columns, onChange]);

  const handleSort = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over === null) {
      return;
    }
    if (active.id === over.id) {
      return;
    }

    const oldIndex = columns.findIndex((it) => it.key === active.id);
    const newIndex = columns.findIndex((it) => it.key === over.id);

    const ordered = arrayMove(columns.slice(), oldIndex, newIndex);
    onChange(ordered);
  }, [columns, onChange]);

  return (
    <div className="bp-project-list-column-selector">
      <div className="bp-project-list-column-selector__header">
        <BodyMedium01>
          <FormattedMessage id="projectList/toolbar/header"/>
        </BodyMedium01>
      </div>

      <div className="bp-project-list-column-selector__item">
        <div className="bp-project-list-column-selector__column">
          <ProjectListColumnToggle
            name="project.list.column.title"
            label={<FormattedMessage id="projectList/toolbar/columns/title"/>}
            checked={true}
            disabled={true}
            onChange={constVoid}
          />
        </div>
      </div>

      <DndContext
        sensors={sortableSensors}
        modifiers={sortableModifiers}
        onDragEnd={handleSort}
      >
        <SortableContext
          items={sortableItems}
          strategy={sortableStrategy}
        >
          {columns.map((column) => (
            <SortableColumn
              key={column.key}
              column={column}
              onChange={handleToggle}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="bp-project-list-column-selector__item">
        <div className="bp-project-list-column-selector__column">
          <ProjectListColumnToggle
            name="project.list.column.rating"
            label={<FormattedMessage id="projectList/toolbar/columns/energyEfficiency"/>}
            checked={true}
            disabled={true}
            onChange={constVoid}
          />
        </div>
      </div>
    </div>
  );
});

type ColumnProps = {
  readonly column: ProjectListColumn;
  readonly onChange: (column: ProjectListColumn) => void;
};

const SortableColumn = memo(({ column, onChange }: ColumnProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    active,
  } = useSortable({
    id: column.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  const handleToggle = useCallback((checked: boolean) => {
    onChange({ key: column.key, enabled: checked });
  }, [column.key, onChange]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'bp-project-list-column-selector__item',
        active?.id === column.key ? 'bp-project-list-column-selector__item--dragging' : null,
      )}
      {...attributes}

    >
      <div className="bp-project-list-column-selector__column">
        <ProjectListColumnToggle
          name={`project.list.column.${column.key}`}
          label={COLUMN_NAMES[column.key]}
          checked={column.enabled}
          disabled={false}
          onChange={handleToggle}
        />
      </div>
      <div className="bp-project-list-column-selector__handle">
        <div ref={setActivatorNodeRef} className="bp-project-list-column-selector__icon" {...listeners}>
          <SortHandleIcon/>
        </div>
      </div>
    </div>
  );
});

const COLUMN_NAMES: Record<ProjectListColumnKey, React.ReactElement> = {
  [ProjectListColumnKey.MEMBERS]: <FormattedMessage id="projectList/toolbar/columns/members"/>,
  [ProjectListColumnKey.STATUS]: <FormattedMessage id="projectList/toolbar/columns/status"/>,
  [ProjectListColumnKey.UPDATED]: <FormattedMessage id="projectList/toolbar/columns/updatedAt"/>,
  [ProjectListColumnKey.ROI]: <FormattedMessage
    id="projectList/toolbar/columns/ROI_excl_CO2_10"
    values={{ sub, sup }}
  />,
  [ProjectListColumnKey.ROI_2]: <FormattedMessage
    id="projectList/toolbar/columns/ROI_excl_CO2_2"
    values={{ sub, sup }}
  />,
  [ProjectListColumnKey.ROI_5]: <FormattedMessage
    id="projectList/toolbar/columns/ROI_excl_CO2_5"
    values={{ sub, sup }}
  />,
  [ProjectListColumnKey.BEP]: <FormattedMessage id="projectList/toolbar/columns/BEP_excl_CO2" values={{ sub, sup }}/>,
};
